import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { AppDispatch, GlobalState, ThunkApi } from '../store'
import type { Api } from '../api/Api'
import { enqueueErrorSnackbar } from '../redux/actions/notificationActions'
import { withDefaultPagination } from '.'
import { ConfirmDialogContext } from '../components/common/ConfirmDialog'
import type { Appliance, ListResult } from 'common/api/v1/types'
import { PortType } from 'common/api/v1/types'
import { clearInputs, getInputs } from '../redux/actions/inputsActions'
import { clearOutputs, getOutputs } from '../redux/actions/outputsActions'
import { clearGroups, getGroups } from '../redux/actions/groupActions'
import { clearPorts, getPorts } from '../redux/actions/portActions'
import { clearAppliances, getAppliances } from '../redux/actions/applianceActions'
import { clearOutputLists, getOutputLists } from '../redux/actions/outputListsActions'
import { getGroupLists } from '../redux/actions/groupListsActions'
import { getAudit } from '../redux/actions/auditActions'
import { getAlarmLogs } from '../redux/actions/alarmLogsActions'
import { makeApplicationException } from '../api/ApplicationException'
import {
  AlarmsRequestParams,
  AppliancesRequestParams,
  AuditLogRequestParams,
  EnrichedUserWithSettings,
  GroupsRequestParams,
  InputsRequestParams,
  KubernetesNodesRequestParams,
  AdBreaksRequestParams,
  NetworksRequestParams,
  OutputsRequestParams,
  PaginatedRequestParams,
  PhysicalPortInfoWithAppliance,
  PortsRequestParams,
  TransformationProfilesRequestParams,
} from '../api/nm-types'
import { AsyncThunk } from '@reduxjs/toolkit'
import { parseSearchParams } from './params'
import { clearAlarms, getAlarms } from '../redux/actions/alarmsActions'
import { clearKubernetesNodes, getKubernetesNodes } from '../redux/actions/k8sActions'
import { getNetworks } from '../redux/actions/networkActions'
import { getSettings } from '../redux/actions/settingsActions'
import { readUsage } from '../redux/actions/usageActions'
import { PortDirection } from 'common/types'
import { listAdCreatives } from '../redux/actions/adCreativeActions'
import { listAdPods } from '../redux/actions/adPodActions'
import { listAdBreaks } from '../redux/actions/adBreakActions'
import { listTransformationProfiles } from '../redux/actions/transformationProfileActions'
import { getAdAudit } from '../redux/actions/adAuditActions'
import { listOverlays } from '../redux/actions/overlaysActions'
import { listAdServers } from '../redux/actions/adServerActions'

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>(undefined)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

/**
 * returns the function to be called to invoke common confirmation dialog from the context
 * function parameters are
 *  callback: callback to call on OK click,
 *  comp: component to show inside the dialog (confirm text),
 *  confirmText?: text the user has to type to confirm the action,
 *  buttonProps?: {ok: {text, ...other button props}, cancel: {text, ...other button props}},
 *  cancelCallback?: callback to call on Cancel click,
 */
export const useConfirmationDialog = () => {
  const { showConfirmation } = useContext(ConfirmDialogContext)
  return showConfirmation
}

export function usePageParams<PageParameters = Record<string, string | undefined>>() {
  const navigate = useNavigate()
  const location = useLocation()

  const pageParams = useMemo(() => parseSearchParams(location.search), [location.search])

  const setPageParams = (newParams: PageParameters | undefined) => {
    if (!newParams) {
      navigate(location.pathname, { replace: true })
      return
    }
    const currentParams = new URLSearchParams(location.search)
    for (const key in newParams) {
      const value = newParams[key]?.toString()
      if (!value) {
        currentParams.delete(key)
      } else {
        currentParams.delete(key) // Delete and set to maintain same order as in newParams
        currentParams.set(key, value)
      }
    }
    const search = currentParams.toString()
    if (!search) {
      return navigate(location.pathname, { replace: true })
    }
    return navigate(location.pathname + '?' + search, { replace: true })
  }

  return [pageParams, setPageParams] as [PageParameters, typeof setPageParams]
}

// Non-react-js debounce version
// Return the latest value if it's been more than <delay>ms since it was last called.
// Otherwise, it will return the previous value.
export function useDebounce<S>(value: S, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    if (delay > 0) {
      const handler = setTimeout(() => setDebouncedValue(value), delay)
      return () => clearTimeout(handler)
    } else {
      setDebouncedValue(value)
    }
  }, [value])

  return debouncedValue
}

// Call the passed function when it hasn't been called for the wait period
export const useDebouncedCallback = (func: Parameters<typeof useCallback>[0], delay = 200) => {
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  return useCallback(
    (...args: any[]) => {
      const later = () => {
        clearTimeout(timeout.current)
        func(...args)
      }

      clearTimeout(timeout.current)
      timeout.current = setTimeout(later, delay)
    },
    [func, delay],
  )
}

/**
 * Adding the debounce on input change (mostly used for filter)
 * @param filter: the new value from changed input
 * @param onChange: if provided, invokes on debounced value changed
 * @param onInit: if we should invoke callback on init
 */
export const useDebouncedFilter = (filter: string, onChange?: (debounced: string) => void, onInit = false) => {
  const [debouncedFilter, setDebouncedFilter] = useState<string>(filter)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const clearTimer = () => timer.current && clearTimeout(timer.current)
  useEffect(() => {
    clearTimer()
    timer.current = setTimeout(() => setDebouncedFilter(filter), 500)
    return clearTimer
  }, [filter])

  const [initial, setInitial] = useState(!onInit)
  useEffect(() => {
    onChange && !initial && onChange(debouncedFilter)
    setInitial(false)
  }, [debouncedFilter])

  return debouncedFilter
}

/**
 * helper to get the entity selector based on api requests
 * @param action: action to dispatch to get entities
 * @param reducer: reducer name from the redux store to get values from
 * @param requestParams: api request params to dispatch action with
 */
const useEntitySelector = <TReducer extends keyof GlobalState, TParams extends PaginatedRequestParams>(
  action: AsyncThunk<any, any, ThunkApi>,
  reducer: TReducer,
  requestParams: TParams,
) => {
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    switch (reducer) {
      case 'inputsReducer':
        dispatch(clearInputs())
        break
      case 'outputsReducer':
        dispatch(clearOutputs())
        break
      case 'groupsReducer':
        dispatch(clearGroups())
        break
      case 'alarmsReducer':
        dispatch(clearAlarms())
        break
      case 'portsReducer':
        dispatch(clearPorts())
        break
      case 'appliancesReducer':
        dispatch(clearAppliances())
        break
      case 'outputListsReducer':
        dispatch(clearOutputLists())
        break
      case 'k8sReducer':
        dispatch(clearKubernetesNodes())
        break
      default:
    }
  }, [dispatch])

  useEffect(() => {
    const promise = dispatch(action(requestParams))
    return () => {
      promise.abort()
    }
  }, [dispatch, Object.entries(requestParams).join(',')])
  return useSelector((state: GlobalState) => state[reducer], shallowEqual)
}

export const useInputsSelector = (requestParams: InputsRequestParams) =>
  useEntitySelector(getInputs, 'inputsReducer', requestParams)
export const useOutputsSelector = (requestParams: OutputsRequestParams) =>
  useEntitySelector(getOutputs, 'outputsReducer', requestParams)
export const useGroupsSelector = (requestParams: GroupsRequestParams) =>
  useEntitySelector(getGroups, 'groupsReducer', requestParams)
export const useSettingsSelector = () =>
  useEntitySelector(getSettings, 'settingsReducer', { pageNumber: '0', rowsPerPage: '0' })
export const useAlarmsSelector = (requestParams: AlarmsRequestParams) =>
  useEntitySelector(getAlarms, 'alarmsReducer', requestParams)
export const useAlarmLogsSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(getAlarmLogs, 'alarmLogsReducer', requestParams)
export const useInterfacesSelector = (requestParams: PortsRequestParams) =>
  useEntitySelector(getPorts, 'portsReducer', requestParams)
export const useApplianceSelector = (requestParams: AppliancesRequestParams) =>
  useEntitySelector(getAppliances, 'appliancesReducer', requestParams)
export const useOutputListsSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(getOutputLists, 'outputListsReducer', requestParams)
export const useGroupListsSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(getGroupLists, 'groupListsReducer', requestParams)
export const useAuditSelector = (requestParams: AuditLogRequestParams) =>
  useEntitySelector(getAudit, 'auditReducer', requestParams)
export const useAdAuditSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(getAdAudit, 'adAuditReducer', requestParams)
export const useAdCreativeSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(listAdCreatives, 'adCreativeReducer', requestParams)
export const useAdPodsSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(listAdPods, 'adPodReducer', requestParams)
export const useAdBreaksSelector = (requestParams: AdBreaksRequestParams) =>
  useEntitySelector(listAdBreaks, 'adBreakReducer', requestParams)
export const useAdServerSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(listAdServers, 'adServerReducer', requestParams)
export const useKubernetesNodesSelector = (requestParams: KubernetesNodesRequestParams) =>
  useEntitySelector(getKubernetesNodes, 'k8sReducer', requestParams)
export const useNetworksSelector = (requestParams: NetworksRequestParams) =>
  useEntitySelector(getNetworks, 'networksReducer', requestParams)
export const useUsageSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(readUsage, 'usageReducer', requestParams)
export const useTransformationProfilesSelector = (requestParams: TransformationProfilesRequestParams) =>
  useEntitySelector(listTransformationProfiles, 'transformationProfileReducer', requestParams)
export const useOverlaysSelector = (requestParams: PaginatedRequestParams) =>
  useEntitySelector(listOverlays, 'overlaysReducer', requestParams)

export const usePageParamsFiltered = <TParams extends PaginatedRequestParams>(): TParams => {
  const [params] = usePageParams()
  return withDefaultPagination(params) as TParams
}

/**
 * Creates own pagination getting state, returns state
 * @param params: params to send request with
 * @param api: api helper to make request
 * @param updateOn: if switches from false to true or back, forces to update state
 * @param ignore: condition whether we should stop requesting on parameters change
 */
export const useSelfStatePagination = <TEntity, TParams extends PaginatedRequestParams>({
  params,
  api,
  updateOn,
  ignore = false,
}: {
  params: Partial<TParams>
  api: (params: TParams) => Promise<ListResult<TEntity>>
  updateOn?: boolean
  ignore?: boolean
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { filter = '', ...rest } = params
  const debouncedFilter = useDebouncedFilter(filter)
  const [state, setState] = useState<ListResult<TEntity>>({ items: [], total: 0 })
  const [loading, setLoading] = useState(false)
  const [updateFlag, setUpdateFlag] = useState(false)
  const [isFirst, setIsFirst] = useState(true)

  useEffect(() => {
    if (!isFirst) setUpdateFlag(!updateFlag)
    setIsFirst(false)
  }, [updateOn])

  const fetchPage = async () => {
    setLoading(true)
    try {
      const parameters = { filter: debouncedFilter, ...rest }
      const withDefault = withDefaultPagination(parameters) as TParams
      const res = await api(withDefault)
      setState(res)
    } catch (error) {
      const applicationException = makeApplicationException(error)
      dispatch(enqueueErrorSnackbar({ error: applicationException }))
    }
    setLoading(false)
  }

  useEffect(() => {
    // Prevent multiple changes (i.e. change of filter + something else) to fire 2 requests: 1 immediately and the second one after debounce :
    // This effect is triggered one or more properties have changed, e.g. either filter, pageNumber, rowsPerPage, etc.
    // If something other than the filter has changed, e.g. pageNumber, then we go ahead and fetch.
    // If the filter has changed (either alone or together with other properties, e.g. pageNumber) we wait until the debounce.
    const shouldWaitForDebounce = debouncedFilter != filter
    if (shouldWaitForDebounce) return

    !ignore && fetchPage()
  }, [
    Object.values({ ...rest, updateFlag })
      .concat(debouncedFilter)
      .join(','),
  ])

  return { ...state, loading }
}

/**
 * Returns current user
 */
export const useUser = (): EnrichedUserWithSettings => {
  return useSelector(({ userReducer }: GlobalState) => userReducer.user as EnrichedUserWithSettings, shallowEqual)
}

/**
 * Returns whether the current user has type-to-confirm enabled
 * @returns true only if user settings are loaded AND typeToConfirmEnabled is explicitly true
 * This prevents type-to-confirm from activating during loading states or in tests
 */
export const useTypeToConfirmSetting = (): boolean => {
  const user = useUser()
  return user.settings?.typeToConfirmEnabled === true
}

// Copied from https://usehooks-typescript.com/react-hook/use-interval
export const useIntervalNoInitial = (callback: () => Promise<void>, delay: number | null) => {
  const savedCallback = useRef(callback)

  // Remember the latest callback if it changes.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    if (delay === null) {
      return
    }

    const id = setInterval(() => {
      savedCallback.current()
    }, delay)

    return () => clearInterval(id)
  }, [delay])
}

export function useInterval(callback: () => Promise<void>, delay: number) {
  const callbackRef = useRef(callback)
  const timerIdRef = useRef<NodeJS.Timeout | null>(null)
  const isCancelledRef = useRef(false)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const clearTimer = () => {
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current)
      timerIdRef.current = null
    }
  }

  const tick = useCallback(async () => {
    if (isCancelledRef.current) return

    try {
      await callbackRef.current()
    } catch {
      // Ignore errors
    }

    if (isCancelledRef.current) return

    timerIdRef.current = setTimeout(() => void tick(), delay)
  }, [delay])

  // Manual trigger: runs immediately and restarts timer
  const trigger = useCallback(() => {
    clearTimer()
    void tick()
  }, [tick])

  useEffect(() => {
    isCancelledRef.current = false
    void tick()

    return () => {
      isCancelledRef.current = true
      clearTimer()
    }
  }, [tick])

  return { trigger }
}

type UseIntervalResult<T> =
  | { hasResult: false; trigger: () => void }
  | { hasResult: true; result: T; trigger: () => void }

export function useIntervalState<T>(callback: () => Promise<T>, delay: number): UseIntervalResult<T> {
  const [state, setState] = useState<T>()
  const { trigger } = useInterval(async () => {
    setState(await callback())
  }, delay)

  return state === undefined ? { hasResult: false, trigger } : { hasResult: true, result: state, trigger }
}

export const useAppliancePhysicalPorts = (
  selectedAppliance: Pick<Appliance, 'id' | 'name'> | undefined,
  applianceApi: Api['appliancesApi'],
  direction?: PortDirection,
  requestingServiceId?: string,
): {
  result?: PhysicalPortInfoWithAppliance[]
  error?: Error | null
  loading: boolean
} => {
  const { isLoading, data, error } = useQuery({
    enabled: !!selectedAppliance?.id,
    queryKey: ['ports', selectedAppliance?.id],
    queryFn: async () => {
      const { physicalPorts, appliance } = await applianceApi.listAppliancePhysicalPorts(selectedAppliance!.id)
      return physicalPorts
        .filter((p) => !direction || !p.direction || p.direction === direction)
        .map((p) => {
          const conflictingPorts = p.occupiedPorts.filter((op) => {
            const belongsToOtherInput = !!op.inputId && op.inputId !== requestingServiceId
            const belongsToOtherOutput = !!op.outputId && op.outputId !== requestingServiceId
            const belongsToOtherTunnel = !!op.tunnelId && op.tunnelId.toString() !== requestingServiceId
            return belongsToOtherInput || belongsToOtherOutput || belongsToOtherTunnel // else belongs to the requesting input/output/tunnel - i.e. not conflicting
          })
          return { ...p, occupiedPorts: conflictingPorts, appliance }
        })
        .sort((p1, p2) => {
          if (p1.portType !== PortType.ip && p2.portType === PortType.ip) return -1
          if (p2.portType !== PortType.ip && p1.portType === PortType.ip) return 1
          return 0
        })
    },
    meta: { operation: 'fetch ports' },
  })

  return { result: data, error, loading: isLoading }
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

export function useMobile() {
  const windowDimensions = useWindowDimensions()
  return { isMobile: windowDimensions.width < 768 }
}
